import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowUpRight,
  FileText,
  ReceiptText,
  GitCompareArrows,
  DatabaseZap,
  LucideIcon,
  Search,
} from "lucide-react";
import apps, { AppConfig, APP_SECTIONS } from "../../config/apps";
import { useAuth } from "../../contexts/AuthContext";

const ICON_MAP: Record<string, LucideIcon> = {
  FileText,
  ReceiptText,
  GitCompareArrows,
  DatabaseZap,
};

type Accent = { solid: string; soft: string };

const ACCENT: Record<string, Accent> = {
  "from-blue-500 to-cyan-400": { solid: "#0a6ed1", soft: "#e8f3fb" },
  "from-violet-500 to-purple-400": { solid: "#5b3fd6", soft: "#eeebfc" },
  "from-emerald-500 to-teal-400": { solid: "#107e3e", soft: "#e6f4ec" },
  "from-amber-500 to-orange-400": { solid: "#c25a00", soft: "#fdf0e3" },
};

const FALLBACK = ACCENT["from-blue-500 to-cyan-400"];

const CATEGORY: Record<string, string> = {
  "quote-agent": "Sales · Quotation",
  "invoice-agent": "Order-to-Cash · Billing",
  "three-way-match": "Procure-to-Pay · Matching",
  mdg: "Master Data · Governance",
};

const useGreeting = () => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(id);
  }, []);
  return useMemo(() => {
    const h = now.getHours();
    if (h < 5) return "Working late";
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    if (h < 21) return "Good evening";
    return "Good night";
  }, [now]);
};

const formattedDate = (d: Date) =>
  d.toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

const AppTile: React.FC<{ app: AppConfig }> = ({ app }) => {
  const Icon = ICON_MAP[app.icon] ?? FileText;
  const a = ACCENT[app.accentColor] ?? FALLBACK;
  const category = CATEGORY[app.id] ?? "Application";
  const host = useMemo(() => {
    try {
      return new URL(app.url).hostname.replace(/^www\./, "");
    } catch {
      return app.url;
    }
  }, [app.url]);

  return (
    <button
      type="button"
      onClick={() => window.open(app.url, "_blank", "noopener,noreferrer")}
      className="group flex w-full flex-col bg-white text-left transition-all duration-200 hover:-translate-y-[2px] hover:border-[#0a6ed1] hover:shadow-[0_8px_24px_-12px_rgba(10,29,55,0.18)] focus:outline-none focus-visible:border-[#0a6ed1] focus-visible:ring-2 focus-visible:ring-[#0a6ed1]/30"
      style={{
        border: "1px solid #e5e7eb",
        borderRadius: 6,
        minHeight: 156,
      }}
      aria-label={`Open ${app.name}`}
    >
      <div className="flex h-full w-full flex-col justify-between gap-4 p-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <span
            className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded"
            style={{ background: a.soft, color: a.solid }}
          >
            <Icon size={18} strokeWidth={2.2} />
          </span>

          <div className="flex items-center gap-2">
            <span className="flex items-center gap-1.5 text-[10.5px] font-medium uppercase tracking-wider text-[#107e3e]">
              <span className="h-1.5 w-1.5 rounded-full bg-[#107e3e]" />
              Active
            </span>
          </div>
        </div>

        {/* Body */}
        <div>
          <p className="mb-1 text-[10.5px] font-medium uppercase tracking-wider text-[#6a6d70]">
            {category}
          </p>
          <h3 className="text-[15px] font-semibold leading-snug tracking-tight text-[#1d2d3e]">
            {app.name}
          </h3>
          <p className="mt-1 line-clamp-2 text-[12.5px] leading-snug text-[#5b6573]">
            {app.description}
          </p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-[#eef0f3] pt-3">
          <span className="font-mono text-[11px] text-[#6a6d70]">{host}</span>
          <span className="flex items-center gap-1 text-[11.5px] font-medium text-[#0a6ed1] transition-colors group-hover:text-[#0854a0]">
            Open
            <ArrowUpRight
              size={13}
              strokeWidth={2.4}
              className="transition-transform duration-200 group-hover:-translate-y-[1px] group-hover:translate-x-[1px]"
            />
          </span>
        </div>
      </div>
    </button>
  );
};

const AppLauncher: React.FC = () => {
  const { profile } = useAuth();
  const greeting = useGreeting();
  const firstName = profile?.name?.split(" ")[0] ?? "there";
  const today = useMemo(() => formattedDate(new Date()), []);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return apps;
    return apps.filter(
      (a) =>
        a.name.toLowerCase().includes(q) ||
        a.description.toLowerCase().includes(q) ||
        (CATEGORY[a.id] ?? "").toLowerCase().includes(q)
    );
  }, [query]);

  const grouped = useMemo(
    () =>
      APP_SECTIONS.map((section) => ({
        ...section,
        apps: filtered.filter((app) => app.section === section.id),
      })).filter((section) => section.apps.length > 0),
    [filtered]
  );

  return (
    <div className="w-full">
      <div className="w-full mx-auto max-w-6xl px-6 py-8 sm:py-10">
        {/* Page header */}
        <header className="mb-8 border-b border-[#e5e7eb] pb-6">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-[#6a6d70]">
                Home / Workspaces
              </p>
              <h1 className="mt-1.5 text-[24px] font-semibold leading-tight tracking-tight text-[#1d2d3e]">
                {greeting}, {firstName}
              </h1>
              <p className="mt-1 text-[13px] text-[#5b6573]">{today}</p>
            </div>

            <div className="relative w-full max-w-xs">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6a6d70]"
              />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search applications"
                className="h-9 w-full rounded border border-[#d9dce0] bg-white pl-8 pr-3 text-[13px] text-[#1d2d3e] placeholder:text-[#9aa0a6] focus:border-[#0a6ed1] focus:outline-none focus:ring-2 focus:ring-[#0a6ed1]/20"
              />
            </div>
          </div>
        </header>

        {filtered.length === 0 ? (
          <div className="rounded border border-dashed border-[#d9dce0] bg-white py-12 text-center">
            <p className="text-[13px] text-[#6a6d70]">
              {`No applications match "${query}"`}
            </p>
          </div>
        ) : (
          <div className="space-y-10">
            {grouped.map((section) => (
              <section key={section.id}>
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-[13px] font-semibold uppercase tracking-[0.12em] text-[#1d2d3e]">
                    {section.title}
                  </h2>
                  <span className="text-[11.5px] text-[#6a6d70]">
                    {section.apps.length}{" "}
                    {section.apps.length === 1 ? "app" : "apps"}
                  </span>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {section.apps.map((app) => (
                    <AppTile key={app.id} app={app} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AppLauncher;
